import { Lead } from '@/types/meta-lead.ts';
import { Badge } from '@/components/ui/badge.tsx';
import { Clock, Workflow, AlertTriangle, MousePointer } from 'lucide-react';
import { formatRelativeDate } from '../utils/leadUtils.ts';

interface LeadTableProps {
    leads: Lead[];
    onView: (lead: Lead) => void;
    selectedIds: Set<number>;
    onToggleSelect: (leadId: number) => void;
}

export default function LeadTable({ leads, onView, selectedIds, onToggleSelect }: LeadTableProps) {
    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-100">
                <thead>
                    <tr className="bg-gradient-to-r from-[#0f172a] to-[#1e293b]">
                        <th className="w-10 px-4 py-4">
                            <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-white/30 text-[#7dd3fc] focus:ring-[#7dd3fc] cursor-pointer"
                                onChange={(e) => {
                                    leads.forEach(l => {
                                        const isSelected = selectedIds.has(l.id);
                                        if (e.target.checked && !isSelected) onToggleSelect(l.id);
                                        if (!e.target.checked && isSelected) onToggleSelect(l.id);
                                    });
                                }}
                                checked={leads.length > 0 && leads.every(l => selectedIds.has(l.id))}
                            />
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-200 uppercase tracking-wider">
                            Nome
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-200 uppercase tracking-wider">
                            Página
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-200 uppercase tracking-wider">
                            Tags
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-200 uppercase tracking-wider">
                            Status
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-200 uppercase tracking-wider">
                            Funil
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-200 uppercase tracking-wider">
                            Clicks
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-200 uppercase tracking-wider">
                            Última Interação
                        </th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-50">
                    {leads.map((lead) => {
                        const fullName = `${lead.nome} ${lead.sobrenome || ''}`.trim();
                        const isInactive = !lead.is_active;
                        const isSelected = selectedIds.has(lead.id);

                        return (
                            <tr
                                key={lead.id}
                                onClick={() => onView(lead)}
                                className={`cursor-pointer transition-all duration-200 group ${isSelected
                                        ? 'bg-blue-50/50'
                                        : isInactive
                                            ? 'bg-red-50/30 hover:bg-red-50/50'
                                            : 'hover:bg-[#f8fafc]'
                                    }`}
                            >
                                {/* Checkbox */}
                                <td className="w-10 px-4 py-4" onClick={(e) => e.stopPropagation()}>
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => onToggleSelect(lead.id)}
                                        className="h-4 w-4 rounded border-gray-300 text-[#0f172a] focus:ring-[#0f172a] cursor-pointer"
                                    />
                                </td>

                                {/* Nome */}
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white ${isInactive
                                                ? 'bg-gradient-to-br from-red-400 to-red-500'
                                                : 'bg-gradient-to-br from-[#0f172a] to-[#1e3a5f]'
                                            }`}>
                                            {lead.nome.charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-900">{fullName}</p>
                                            <p className="text-xs text-gray-400">{lead.sender_id.slice(0, 10)}...</p>
                                        </div>
                                    </div>
                                </td>

                                {/* Página */}
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm font-medium text-gray-700">
                                        {lead.page_name || lead.page_id || '—'}
                                    </span>
                                </td>

                                {/* Tags */}
                                <td className="px-6 py-4">
                                    <div className="flex flex-wrap gap-1">
                                        {lead.tags.slice(0, 2).map((tag) => (
                                            <Badge
                                                key={tag.id}
                                                variant="outline"
                                                style={{
                                                    borderColor: tag.cor || '#ccc',
                                                    color: tag.cor || '#666'
                                                }}
                                                className="text-xs rounded-lg px-2 py-0.5"
                                            >
                                                {tag.nome}
                                            </Badge>
                                        ))}
                                        {lead.tags.length > 2 && (
                                            <span className="text-xs text-gray-400">+{lead.tags.length - 2}</span>
                                        )}
                                        {lead.tags.length === 0 && (
                                            <span className="text-xs text-gray-300">—</span>
                                        )}
                                    </div>
                                </td>

                                {/* Status */}
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {isInactive ? (
                                        <Badge className="bg-gradient-to-r from-red-50 to-red-100 text-red-700 border border-red-200/50 rounded-lg text-xs">
                                            <AlertTriangle className="w-3 h-3 mr-1" />
                                            Não Visado
                                        </Badge>
                                    ) : (
                                        <Badge className={`rounded-lg text-xs border ${lead.in_24h_window
                                                ? 'bg-gradient-to-r from-emerald-50 to-green-100 text-emerald-700 border-emerald-200/50'
                                                : 'bg-gradient-to-r from-orange-50 to-amber-100 text-orange-700 border-orange-200/50'
                                            }`}>
                                            <Clock className="w-3 h-3 mr-1" />
                                            {lead.in_24h_window ? 'Aberta' : 'Fechada'}
                                        </Badge>
                                    )}
                                </td>

                                {/* Funil */}
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {lead.current_funnel_id ? (
                                        <div className="flex items-center gap-1.5 text-sm">
                                            <Workflow className="w-3.5 h-3.5 text-cyan-500" />
                                            <span className="text-cyan-700 font-medium">#{lead.current_funnel_id}</span>
                                        </div>
                                    ) : (
                                        <span className="text-xs text-gray-300">—</span>
                                    )}
                                </td>

                                {/* Clicks */}
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-1.5 text-sm">
                                        <MousePointer className="w-3.5 h-3.5 text-indigo-400" />
                                        <span className="font-semibold text-gray-900">{lead.total_clicks}</span>
                                    </div>
                                </td>

                                {/* Última Interação */}
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {lead.last_interaction_at
                                        ? formatRelativeDate(lead.last_interaction_at)
                                        : <span className="text-gray-300">—</span>
                                    }
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
